import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../context/authContext';
import { optionsCategoria, optionsEstado, optionsPartido } from '../datos/options';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Select from 'react-select';

import encabezadoImg from '../components/informes/encabezado_A4.png';
import pieImg from '../components/informes/pie_hoja_A4.png';

// Convierte una imagen local a dataURL para poder incrustarla en el PDF
const cargarImagenComoDataUrl = (src) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const c = document.createElement('canvas');
                c.width = img.naturalWidth;
                c.height = img.naturalHeight;
                c.getContext('2d').drawImage(img, 0, 0);
                resolve({ dataUrl: c.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
            } catch (e) {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });

// Formatea un número como moneda en pesos ($ 1.234.567,89)
const formatearMonto = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '$ 0,00';
    return '$' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatearEntero = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR');
};

// Monto conveniado total = monto_obra + adendas (misma convención que los otros informes)
const getMontoConveniado = (obra) => Number(obra?.monto_obra || 0) + Number(obra?.adendas || 0);

const getUbicacion = (obra) => {
    const partido = String(obra?.partido_nombre || '').trim();
    const localidad = String(obra?.lc_21_localidad || '').trim();
    if (partido && localidad && partido.toLowerCase() !== localidad.toLowerCase()) {
        return `${partido} — ${localidad}`;
    }
    return partido || localidad || '—';
};

// Expediente del proyecto (objeto_o === "Proyecto"), igual que en los informes técnicos
const getExpedienteProyecto = (obra) => {
    const proyecto = (obra?.array_obras_expediente || []).find((item) => item && item.objeto_o === 'Proyecto');
    return proyecto?.expediente_o || 'Sin especificar';
};

// Escapa texto para insertarlo en el HTML que Excel va a abrir como .xls
const escaparHtml = (valor) =>
    String(valor ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/[\r\n]+/g, ' ');

const IntervencionClubesDeportivosPage = () => {
    const { token, role } = useContext(AuthContext);

    const [categorias, setCategorias] = useState(['deportiva']);
    const [q, setQ] = useState('');
    const [partido, setPartido] = useState('');
    const [estados, setEstados] = useState([]);
    const [obras, setObras] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [generado, setGenerado] = useState(false);
    const [error, setError] = useState('');
    const [descargando, setDescargando] = useState(false);

    const pdfRef = useRef(null);

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const categoriaLabel =
        categorias.length === 0
            ? 'Sin categorías'
            : categorias.length === optionsCategoria.length
            ? 'Todas las categorías'
            : optionsCategoria
                  .filter((c) => categorias.includes(c.value))
                  .map((c) => c.label)
                  .join(', ');

    const partidoLabel = optionsPartido.find((p) => p.value === partido)?.label || '';
    const estadoLabel = optionsEstado
        .filter((e) => estados.includes(e.value))
        .map((e) => e.label)
        .join(', ');
    const estadoOptions = optionsEstado.map((e) => ({ value: e.value, label: e.label }));

    const nombreArchivo = [
        'clubes_deportivos',
        categorias.join('-') || 'sin-categoria',
        partidoLabel ? `partido-${partidoLabel}` : null,
        estadoLabel ? `estado-${estadoLabel}` : null,
    ]
        .filter(Boolean)
        .join('_')
        .replace(/[^a-z0-9_-]+/gi, '_');

    const toggleCategoria = (value) => {
        setCategorias((prev) => (prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]));
    };

    const cargar = async (params) => {
        if (!params.categorias || params.categorias.length === 0) {
            setError('Seleccioná al menos una categoría de ONG.');
            setGenerado(false);
            setObras([]);
            return;
        }
        setCargando(true);
        setError('');
        setGenerado(false);
        try {
            const query = new URLSearchParams();
            query.set('categoria', params.categorias.join(','));
            if (params.q) query.set('q', params.q);
            if (params.partido) query.set('partido', params.partido);
            (params.estados || []).forEach((e) => query.append('estado', e));
            const res = await fetch(`/api/informe_clubes_deportivos?${query.toString()}`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setObras(Array.isArray(data) ? data : []);
            setGenerado(true);
            if ((data || []).length === 0) {
                setError('No se encontraron obras para los filtros seleccionados.');
            }
        } catch (e) {
            console.error('Error al generar el informe de clubes deportivos:', e);
            setError(e.message);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar({ categorias: ['deportiva'], q: '', partido: '', estados: [] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadPDF = async () => {
        try {
            setDescargando(true);
            const element = pdfRef.current;
            if (!element) return;

            const canvas = await html2canvas(element, {
                scale: 2,
                backgroundColor: '#ffffff',
                onclone: (doc) => {
                    doc.querySelectorAll('[data-pdf-omit="true"]').forEach((el) => el.remove());
                },
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const [encabezado, pie] = await Promise.all([
                cargarImagenComoDataUrl(encabezadoImg),
                cargarImagenComoDataUrl(pieImg),
            ]);
            const encabezadoHeight = encabezado ? pdfWidth * (encabezado.height / encabezado.width) : 0;
            const pieHeight = pie ? pdfWidth * (pie.height / pie.width) : 0;

            const margenH = 10;
            const margenTop = 10;
            const margenBottom = 10;
            const gapEncabezado = 4;
            const contentWidthMM = pdfWidth - 2 * margenH;
            const contentLeft = margenH;
            const yEncabezado = margenTop;
            const yContenido = yEncabezado + encabezadoHeight + gapEncabezado;
            const pieBottomY = pdfHeight - margenBottom - pieHeight;
            const contentHeightMM = pieBottomY - yContenido;

            const mmPerPx = contentWidthMM / canvas.width;
            const contentPagePx = contentHeightMM / mmPerPx;
            const totalPages = Math.max(1, Math.ceil(canvas.height / contentPagePx));

            for (let i = 0; i < totalPages; i++) {
                if (i > 0) pdf.addPage();

                const remaining = canvas.height - i * contentPagePx;
                const sliceH = Math.min(contentPagePx, remaining);
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = Math.ceil(sliceH);
                const ctx = pageCanvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
                ctx.drawImage(canvas, 0, i * contentPagePx, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

                if (encabezado) {
                    pdf.addImage(encabezado.dataUrl, 'PNG', contentLeft, yEncabezado, contentWidthMM, encabezadoHeight);
                }

                pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', contentLeft, yContenido, contentWidthMM, sliceH * mmPerPx);

                if (pie) {
                    pdf.addImage(pie.dataUrl, 'PNG', contentLeft, pieBottomY, contentWidthMM, pieHeight);
                }
            }

            pdf.save(`${nombreArchivo}.pdf`);
        } catch (error) {
            console.error('Error al generar el PDF:', error);
        } finally {
            setDescargando(false);
        }
    };

    const handleDescargarXLS = () => {
        try {
            const encabezados = [
                'Organización',
                'Categoría',
                'Obra',
                'Exp. de Proyecto',
                'Monto conveniado',
                'Adenda',
                'Total',
                'Estado',
                'Ubicación',
            ];
            // Los montos van como TEXTO ya formateado (punto para miles y coma
            // para decimales), igual que en la tabla. Antes iban como número con
            // mso-number-format, y el "54952309.88" del HTML lo interpretaba
            // Excel según el idioma del sistema (punto como separador de miles),
            // así que se veía mal.
            const COLS_MONTO = [4, 5, 6];
            const celda = (valor, i) =>
                COLS_MONTO.includes(i)
                    ? `<td>${escaparHtml(formatearMonto(valor))}</td>`
                    : `<td>${escaparHtml(valor)}</td>`;
            const filaHtml = (celdas) => `<tr>${celdas.map((valor, i) => celda(valor, i)).join('')}</tr>`;

            const thead = `<tr>${encabezados.map((h) => `<th>${escaparHtml(h)}</th>`).join('')}</tr>`;

            const filas = [];
            let totalConveniado = 0;
            let totalAdenda = 0;
            let totalGeneral = 0;

            for (const grupo of grupos) {
                let subConveniado = 0;
                let subAdenda = 0;

                for (const obra of grupo.obras) {
                    const conveniado = Number(obra?.monto_obra || 0);
                    const adenda = Number(obra?.adendas || 0);
                    subConveniado += conveniado;
                    subAdenda += adenda;
                    filas.push(
                        filaHtml([
                            grupo.organizacion,
                            grupo.ong_categoria || '',
                            obra?.nombre_obra || '',
                            getExpedienteProyecto(obra),
                            conveniado,
                            adenda,
                            conveniado + adenda,
                            obra?.estado || '',
                            getUbicacion(obra),
                        ])
                    );
                }

                const subTotal = subConveniado + subAdenda;
                filas.push(
                    `<tr><td colspan="4">Subtotal ${escaparHtml(grupo.organizacion)} (${grupo.obras.length} ${
                        grupo.obras.length === 1 ? 'obra' : 'obras'
                    })</td>` +
                        `<td>${escaparHtml(formatearMonto(subConveniado))}</td>` +
                        `<td>${escaparHtml(formatearMonto(subAdenda))}</td>` +
                        `<td>${escaparHtml(formatearMonto(subTotal))}</td>` +
                        `<td colspan="2"></td></tr>`
                );

                totalConveniado += subConveniado;
                totalAdenda += subAdenda;
                totalGeneral += subTotal;
            }

            const totalRow =
                `<tr><td colspan="4">Total general</td>` +
                `<td>${escaparHtml(formatearMonto(totalConveniado))}</td>` +
                `<td>${escaparHtml(formatearMonto(totalAdenda))}</td>` +
                `<td>${escaparHtml(formatearMonto(totalGeneral))}</td>` +
                `<td colspan="2"></td></tr>`;

            const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Informe</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table border="1"><thead>${thead}</thead><tbody>${filas.join(
                ''
            )}${totalRow}</tbody></table></body></html>`;

            const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${nombreArchivo}.xls`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al generar el XLS:', error);
        }
    };

    const grupos = useMemo(() => {
        const map = new Map();
        for (const obra of obras) {
            const key = String(obra?.ong_id ?? obra?.organizacion ?? 'sin-ong');
            if (!map.has(key)) {
                map.set(key, {
                    ong_id: obra?.ong_id,
                    organizacion: obra?.organizacion || '(sin nombre)',
                    ong_categoria: obra?.ong_categoria,
                    obras: [],
                });
            }
            map.get(key).obras.push(obra);
        }
        return Array.from(map.values());
    }, [obras]);

    const totalInversion = obras.reduce((acc, o) => acc + getMontoConveniado(o), 0);

    if (role !== 'admin') {
        return (
            <div className="p-8 text-center text-gray-500">
                Acceso denegado. Solo administradores.
            </div>
        );
    }

    return (
        <div className="p-6 w-full max-w-full">
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Informe de Organizaciones
            </h1>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-3">
                <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Categorías de ONG
                    </span>
                    <div className="flex flex-wrap gap-3 px-1 py-2">
                        {optionsCategoria.map((c) => (
                            <label
                                key={c.value}
                                className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100"
                            >
                                <input
                                    type="checkbox"
                                    checked={categorias.includes(c.value)}
                                    onChange={() => toggleCategoria(c.value)}
                                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                />
                                {c.label}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col flex-1 min-w-[260px]">
                    <label htmlFor="busqueda" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Buscar obra / alias
                    </label>
                    <input
                        id="busqueda"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') cargar({ categorias, q: q.trim(), partido, estados });
                        }}
                        placeholder="club, deportivo, polideportivo, playón..."
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    />
                </div>

                <div className="flex flex-col">
                    <label htmlFor="filtro-partido" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Partido (ubicación)
                    </label>
                    <select
                        id="filtro-partido"
                        value={partido}
                        onChange={(e) => setPartido(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    >
                        <option value="">Todos los partidos</option>
                        {optionsPartido.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col min-w-[260px]">
                    <label htmlFor="filtro-estado" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Estado
                    </label>
                    <Select
                        inputId="filtro-estado"
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        options={estadoOptions}
                        value={estadoOptions.filter((op) => estados.includes(op.value))}
                        onChange={(seleccionados) => setEstados((seleccionados || []).map((op) => op.value))}
                        placeholder="Todos los estados..."
                        noOptionsMessage={() => 'Sin resultados'}
                        className="text-sm"
                        styles={{
                            control: (base) => ({ ...base, minHeight: '38px' }),
                            menuPortal: (base) => ({ ...base, zIndex: 60 }),
                        }}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    />
                </div>

                <button
                    onClick={() => cargar({ categorias, q: q.trim(), partido, estados })}
                    disabled={cargando}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                >
                    {cargando ? 'Generando...' : 'Generar informe'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4">{error}</div>
            )}

            {generado && obras.length > 0 && (
                <div className="mb-4 flex justify-end gap-3">
                    <button
                        onClick={handleDescargarXLS}
                        className="inline-flex items-center px-4 py-2 border border-teal-600 text-sm font-medium rounded-md shadow-sm text-teal-700 bg-white hover:bg-teal-50"
                    >
                        Descargar XLS
                    </button>
                    <button
                        onClick={handleDownloadPDF}
                        disabled={descargando}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                    >
                        {descargando ? 'Generando PDF...' : 'Descargar PDF'}
                    </button>
                </div>
            )}

            {generado && obras.length > 0 && (
                <div className="bg-white p-8 px-10 sm:p-12 shadow-xl rounded-lg border border-gray-200">
                    <div ref={pdfRef} className="font-sans">
                        <div className="mb-6" data-pdf-omit="true">
                            <img src={encabezadoImg} alt="Encabezado del informe" className="w-full h-auto" />
                        </div>

                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
                                Gestión Axel Kicillof.
                            </h1>
                            <h2 className="text-xl font-semibold text-gray-700">
                                Organizaciones: {categoriaLabel}
                            </h2>
                            <h3 className="text-base font-medium text-gray-600 mt-1">
                                Informe de intervenciones en clubes deportivos
                                {partidoLabel ? ` — partido: ${partidoLabel}` : ''}
                                {estadoLabel ? ` — estado: ${estadoLabel}` : ''}
                                {q ? ` — búsqueda: "${q}"` : ''}
                            </h3>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Inversión total
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearMonto(totalInversion)}</p>
                            </div>
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Cantidad de obras
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearEntero(obras.length)}</p>
                            </div>
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Organizaciones
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearEntero(grupos.length)}</p>
                            </div>
                        </div>

                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-teal-700 text-white">
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium">
                                        Organización
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-28">
                                        Categoría
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium">
                                        Obra
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-52">
                                        Exp. de Proyecto
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-36">Monto</th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-32">Estado</th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-48">Ubicación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grupos.map((grupo, gi) => {
                                    const subtotal = grupo.obras.reduce((acc, o) => acc + getMontoConveniado(o), 0);
                                    return (
                                        <Fragment key={grupo.ong_id ?? grupo.organizacion}>
                                            {grupo.obras.map((obra) => {
                                                const adendas = Number(obra?.adendas || 0);
                                                const monto = Number(obra?.monto_obra || 0);
                                                const codigo = String(obra?.id_partido_obra || '').trim();
                                                return (
                                                    <tr
                                                        key={obra?.id}
                                                        className={gi % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                                    >
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            <div className="font-medium">{grupo.organizacion}</div>
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-700 capitalize">
                                                            {grupo.ong_categoria || '—'}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            <div className="font-medium">
                                                                {obra?.nombre_obra || '(sin nombre)'}
                                                            </div>
                                                            {codigo ? (
                                                                <div className="text-xs text-gray-500">{codigo}</div>
                                                            ) : null}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800 text-xs break-all">
                                                            {getExpedienteProyecto(obra)}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-right text-gray-800 whitespace-nowrap">
                                                            {adendas > 0 ? (
                                                                <>
                                                                    <span className="block">{formatearMonto(monto)}</span>
                                                                    <span className="block text-xs text-gray-600">
                                                                        + adenda por {formatearMonto(adendas)}
                                                                    </span>
                                                                    <span className="block text-xs font-medium text-gray-800">
                                                                        Total: {formatearMonto(monto + adendas)}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                formatearMonto(monto)
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            {obra?.estado || '—'}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            {getUbicacion(obra)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className="bg-teal-50">
                                                <td
                                                    className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700"
                                                    colSpan={4}
                                                >
                                                    Subtotal {grupo.organizacion} ({grupo.obras.length}{' '}
                                                    {grupo.obras.length === 1 ? 'obra' : 'obras'})
                                                </td>
                                                <td className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-800 whitespace-nowrap">
                                                    {formatearMonto(subtotal)}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-2" colSpan={2}></td>
                                            </tr>
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-teal-700 text-white">
                                    <td
                                        className="border border-teal-800 px-3 py-2 text-right font-semibold"
                                        colSpan={4}
                                    >
                                        Total general
                                    </td>
                                    <td className="border border-teal-800 px-3 py-2 text-right font-semibold whitespace-nowrap">
                                        {formatearMonto(totalInversion)}
                                    </td>
                                    <td className="border border-teal-800 px-3 py-2" colSpan={2}></td>
                                </tr>
                            </tfoot>
                        </table>

                        <div className="mt-8 mb-6 flex justify-between items-center text-sm text-gray-500">
                            <span>Dirección Provincial de Intervenciones Complementarias</span>
                            <span>Fecha de salida: {new Date().toLocaleDateString('es-AR')}</span>
                        </div>

                        <div className="mb-6" data-pdf-omit="true">
                            <img src={pieImg} alt="Pie de página del informe" className="w-full h-auto" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntervencionClubesDeportivosPage;
